/**
 * Careers page (2026-10-08) — PUBLIC list of open jobs at /careers. No login.
 * Company header from tenant settings; one card per published opening.
 * Keeps ?src / ?p (tracking) for the visit so the apply is tagged correctly.
 * Mobile-first.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, ArrowRight, Briefcase, Clock, MapPin } from 'lucide-react';
import { CareersChrome } from './CareersChrome';
import { fetchJobs, type Company, type PublicJob } from './careersPublicApi';
import { formatEmploymentType, formatExperienceRange } from './careersFormat';
import { formatSalaryLakh, readTracking } from './careersPure';

export function CareersListPage() {
  const { search } = useLocation();
  const [company, setCompany] = useState<Company | null>(null);
  const [jobs, setJobs] = useState<PublicJob[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    readTracking(search);
  }, [search]);

  useEffect(() => {
    let alive = true;
    fetchJobs()
      .then((r) => {
        if (!alive) return;
        setCompany(r.company);
        setJobs(r.jobs);
        document.title = `Careers at ${r.company.name}`;
      })
      .catch((e) => {
        if (!alive) return;
        setError(e.message);
        setJobs([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <CareersChrome company={company}>
      <div className="mb-6 rounded-2xl bg-[#1F5C3A] px-5 py-6 text-white sm:px-7">
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Careers at {company?.name ?? '…'}</h1>
        <p className="mt-1 text-sm text-white/85">{company?.tagline || 'Join our team — see the jobs open right now and apply in two minutes.'}</p>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {jobs === null ? (
        <div className="py-16 text-center text-gray-400">Loading jobs…</div>
      ) : jobs.length === 0 && !error ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white py-16 text-center">
          <Briefcase className="mx-auto mb-3 h-8 w-8 text-gray-300" />
          <p className="font-semibold text-gray-700">No open jobs right now</p>
          <p className="mt-1 text-sm text-gray-400">Please check back soon.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {jobs.map((j) => {
            const exp = j.experience ? formatExperienceRange(j.experience.min, j.experience.max) : null;
            const pay = j.salary ? formatSalaryLakh(j.salary.minLakh, j.salary.maxLakh) : '';
            return (
              <Link
                key={j.id}
                to={`/careers/${j.slug}`}
                data-tour="careers-job-card"
                className="group flex min-w-0 flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:border-[#1F5C3A] hover:shadow-md sm:p-5"
              >
                <h2 className="font-display text-lg font-bold text-gray-900 group-hover:text-[#1F5C3A]">{j.jobTitle}</h2>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                    <MapPin className="h-3 w-3" />
                    {j.location}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                    <Briefcase className="h-3 w-3" />
                    {formatEmploymentType(j.employmentType)}
                  </span>
                  {exp && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-1">
                      <Clock className="h-3 w-3" />
                      {exp}
                    </span>
                  )}
                  {pay && <span className="rounded-full bg-gray-50 px-2 py-1">{pay}</span>}
                </div>
                {j.jd.about && <p className="mt-3 line-clamp-2 text-sm text-gray-600">{j.jd.about}</p>}
                <span className="mt-4 inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold text-[#1F5C3A]">
                  View &amp; apply <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </CareersChrome>
  );
}

export default CareersListPage;
